import { Link } from 'react-router-dom';
import { 
  Zap, 
  Users, 
  Shield, 
  Fuel,
  CheckCircle2,
  ChevronRight,
  Car,
  Battery,
  Gauge,
  Star
} from 'lucide-react';
import SEO from '@/components/SEO';

const VTCRentalPage = () => {
  const benefits = [
    {
      icon: <Fuel className="w-6 h-6" />,
      title: "Coûts réduits",
      description: "Économisez sur le carburant grâce à l'électrique. Jusqu'à 70% d'économies par rapport au diesel."
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Confort premium",
      description: "Offrez à vos clients une expérience de transport haut de gamme et silencieuse."
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Véhicule fiable",
      description: "Nos véhicules sont reconnus pour leur fiabilité et leurs faibles coûts d'entretien."
    },
    {
      icon: <Zap className="w-6 h-6" />,
      title: "Parfait pour Uber & Bolt",
      description: "Véhicules éligibles et idéaux pour toutes les plateformes VTC majeures."
    }
  ];

  const vehicles = [
    {
      name: "Tesla Model Y",
      type: "SUV 100% Électrique",
      image: "https://images.unsplash.com/photo-1669625397388-32934837bd3a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHw0fHxUZXNsYSUyME1vZGVsJTIwWSUyMGV4dGVyaW9yJTIwYmxhY2t8ZW58MHx8fHwxNzczMDQxMTYzfDA&ixlib=rb-4.1.0&q=85",
      specs: [
        { label: "Autonomie", value: "350+ km", icon: <Battery className="w-5 h-5" /> },
        { label: "Accélération", value: "0-100 en 5s", icon: <Gauge className="w-5 h-5" /> },
        { label: "Places", value: "5 personnes", icon: <Users className="w-5 h-5" /> },
        { label: "Coffre", value: "854 litres", icon: <Car className="w-5 h-5" /> }
      ],
      features: [
        "Autopilot avancé",
        "Écran tactile 15 pouces",
        "Système audio premium",
        "Climatisation bi-zone",
        "Toit panoramique en verre",
        "Sièges chauffants",
        "Connectivité 4G",
        "Mises à jour OTA"
      ],
      price: "400€",
      color: "Noir"
    },
    {
      name: "Ford Mustang Mach-E",
      type: "SUV 100% Électrique",
      image: "https://customer-assets.emergentagent.com/job_luxeo-tesla-vtc/artifacts/qo7d296n_ford-mustang-mach-e.jpg",
      specs: [
        { label: "Autonomie", value: "440 km", icon: <Battery className="w-5 h-5" /> },
        { label: "Puissance", value: "351 ch", icon: <Gauge className="w-5 h-5" /> },
        { label: "Places", value: "5 personnes", icon: <Users className="w-5 h-5" /> },
        { label: "Coffre", value: "402 litres", icon: <Car className="w-5 h-5" /> }
      ],
      features: [
        "Design sportif Mustang",
        "Écran tactile 15.5 pouces",
        "Système SYNC 4A",
        "Charge rapide 150 kW",
        "Toit panoramique fixe",
        "Sièges chauffants",
        "Ford Co-Pilot360",
        "Apple CarPlay / Android Auto"
      ],
      price: "350€",
      color: "Rouge"
    },
    {
      name: "Mercedes Classe V",
      type: "Van Premium",
      image: "https://customer-assets.emergentagent.com/job_luxeo-tesla-vtc/artifacts/ph7sgr9c_000000007502383.jpg",
      specs: [
        { label: "Capacité", value: "7 places", icon: <Users className="w-5 h-5" /> },
        { label: "Motorisation", value: "Diesel", icon: <Gauge className="w-5 h-5" /> },
        { label: "Confort", value: "Luxe", icon: <Star className="w-5 h-5" /> },
        { label: "Coffre", value: "1030 litres", icon: <Car className="w-5 h-5" /> }
      ],
      features: [
        "Intérieur luxueux",
        "Sièges cuir individuels",
        "Climatisation 3 zones",
        "Système MBUX",
        "Portes coulissantes électriques",
        "Vitres teintées",
        "Idéal pour groupes VIP",
        "Confort exceptionnel"
      ],
      price: "800€",
      color: "Noir"
    }
  ];

  const included = [
    "Assurance tous risques",
    "Assistance 24h/24",
    "Entretien inclus",
    "Carte de recharge (électriques)",
    "Véhicule de remplacement",
    "Kilométrage illimité"
  ];

  return (
    <div data-testid="vtc-rental-page" className="overflow-hidden">
      <SEO 
        title="Location Véhicules VTC Marseille | Tesla Model Y, Mustang Mach-E, Mercedes Classe V | Luxeo Car"
        description="Location de véhicules premium pour chauffeurs VTC à Marseille. Tesla Model Y 400€/sem, Ford Mustang Mach-E 350€/sem, Mercedes Classe V 800€/sem. Assurance et entretien inclus."
        keywords="location Tesla Model Y VTC Marseille, Ford Mustang Mach-E location, Mercedes Classe V chauffeur, véhicule électrique Uber, location longue durée VTC, voiture premium Bolt"
        canonical="/location-vtc"
      />
      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center pt-24 pb-16">
        {/* Background */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1771284848859-12f150fa1637?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwxfHxUZXNsYSUyME1vZGVsJTIwWSUyMHdoaXRlJTIwaW50ZXJpb3IlMjBsdXh1cnl8ZW58MHx8fHwxNzcyOTc1NjY0fDA&ixlib=rb-4.1.0&q=85"
            alt="Tesla Model Y Interior"
            className="w-full h-full object-cover object-center opacity-30"
          />
          <div className="hero-gradient absolute inset-0" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="inline-block font-['Outfit'] text-[#D4AF37] text-sm font-medium uppercase tracking-wider mb-4">
              Location VTC
            </span>
            <h1 
              data-testid="vtc-hero-title"
              className="font-['Outfit'] text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6"
            >
              Véhicules Premium pour{' '}
              <span className="text-gradient-gold">Chauffeurs VTC</span>
            </h1>
            <p className="font-['Manrope'] text-base lg:text-lg text-[#A1A1AA] mb-10 max-w-2xl">
              Louez un véhicule parfaitement adapté à votre activité VTC.
              Tesla Model Y, Ford Mustang Mach-E ou Mercedes Classe V - impressionnez vos clients.
            </p>
            <Link
              to="/contact"
              data-testid="vtc-hero-cta"
              className="btn-primary text-base font-bold px-8 py-4 inline-flex items-center gap-2 group"
            >
              Soumettre votre demande
              <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section data-testid="vtc-benefits-section" className="section-padding bg-[#050505]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-bold text-white mb-4">
              Avantages pour <span className="text-[#D4AF37]">votre activité</span>
            </h2>
            <p className="font-['Manrope'] text-[#A1A1AA] max-w-2xl mx-auto">
              Découvrez pourquoi nos véhicules sont le choix idéal pour les chauffeurs VTC professionnels.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                data-testid={`vtc-benefit-${index}`}
                className="feature-card card-luxeo p-8 group flex gap-6"
              >
                <div className="w-14 h-14 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center text-[#D4AF37] flex-shrink-0 group-hover:bg-[#D4AF37]/20 transition-colors">
                  {benefit.icon}
                </div>
                <div>
                  <h3 className="font-['Outfit'] text-xl font-semibold text-white mb-2">
                    {benefit.title}
                  </h3>
                  <p className="font-['Manrope'] text-[#A1A1AA] text-sm leading-relaxed">
                    {benefit.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Vehicles Detail Section */}
      <section data-testid="vtc-vehicles-section" className="section-padding bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="font-['Outfit'] text-[#D4AF37] text-sm font-medium uppercase tracking-wider mb-4 block">
              Notre Flotte
            </span>
            <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-bold text-white mb-4">
              Véhicules <span className="text-[#D4AF37]">Disponibles</span>
            </h2>
          </div>

          <div className="space-y-20">
            {vehicles.map((vehicle, index) => (
              <div
                key={index}
                data-testid={`vtc-vehicle-detail-${index}`}
                className={`grid lg:grid-cols-2 gap-12 lg:gap-16 items-center ${
                  index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}
              >
                {/* Image */}
                <div className={`vehicle-showcase ${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                  <div className="relative">
                    <img
                      src={vehicle.image}
                      alt={vehicle.name}
                      className="w-full rounded-2xl"
                    />
                    <div className="absolute top-4 right-4 px-4 py-2 rounded-full glass">
                      <span className="font-['Manrope'] text-sm text-white">{vehicle.color}</span>
                    </div>
                  </div>
                </div>

                {/* Info */}
                <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                  <span className="font-['Manrope'] text-[#D4AF37] text-sm mb-2 block">{vehicle.type}</span>
                  <h3 className="font-['Outfit'] text-3xl sm:text-4xl font-bold text-white mb-6">
                    {vehicle.name}
                  </h3>

                  {/* Specs Grid */}
                  <div className="grid grid-cols-2 gap-4 mb-8">
                    {vehicle.specs.map((spec, specIndex) => (
                      <div
                        key={specIndex}
                        className="p-4 rounded-xl glass-light"
                      >
                        <span className="text-[#D4AF37] mb-2 block">{spec.icon}</span>
                        <p className="font-['Outfit'] text-xl font-bold text-white mb-1">
                          {spec.value}
                        </p>
                        <p className="font-['Manrope'] text-[#A1A1AA] text-xs">
                          {spec.label}
                        </p>
                      </div>
                    ))}
                  </div>

                  {/* Features List */}
                  <div className="grid grid-cols-2 gap-2 mb-8">
                    {vehicle.features.map((feature, featureIndex) => (
                      <div
                        key={featureIndex}
                        className="flex items-center gap-2"
                      >
                        <CheckCircle2 className="w-4 h-4 text-[#D4AF37] flex-shrink-0" />
                        <span className="font-['Manrope'] text-white/80 text-sm">
                          {feature}
                        </span>
                      </div>
                    ))}
                  </div>

                  {/* Price & CTA */}
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
                    <div>
                      <p className="font-['Manrope'] text-[#A1A1AA] text-sm">À partir de</p>
                      <p className="font-['Outfit'] text-4xl font-bold text-[#D4AF37]">
                        {vehicle.price}
                        {vehicle.price !== "Sur demande" && <span className="text-lg font-normal text-white/60">/semaine</span>}
                      </p>
                    </div>
                    <Link
                      to="/contact"
                      className="btn-primary text-base font-bold px-8 py-4 inline-flex items-center gap-2 group"
                    >
                      Réserver
                      <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What's Included Section */}
      <section data-testid="vtc-included-section" className="section-padding bg-[#050505]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="pricing-card text-center">
            <span className="font-['Outfit'] text-[#D4AF37] text-sm font-medium uppercase tracking-wider mb-4 block">
              Tout Inclus
            </span>
            <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-bold text-white mb-4">
              Ce qui est compris dans votre location
            </h2>
            <p className="font-['Manrope'] text-[#A1A1AA] mb-10 max-w-lg mx-auto">
              Un tarif transparent, tout inclus, pour vous concentrer sur votre activité.
            </p>

            {/* Included */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-10">
              {included.map((item, index) => (
                <div
                  key={index}
                  data-testid={`vtc-included-${index}`}
                  className="flex items-center gap-2 justify-center sm:justify-start"
                >
                  <CheckCircle2 className="w-5 h-5 text-[#10B981] flex-shrink-0" />
                  <span className="font-['Manrope'] text-white text-sm">
                    {item}
                  </span>
                </div>
              ))}
            </div>

            {/* CTA */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                data-testid="vtc-pricing-cta"
                className="btn-primary text-lg font-bold px-10 py-5 inline-flex items-center gap-2 group"
              >
                Soumettre votre demande
                <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <a
                href="tel:0620202996"
                className="btn-secondary text-lg font-medium px-10 py-5 inline-flex items-center gap-2"
              >
                Appeler le 06 20 20 29 96
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default VTCRentalPage;
